import { colours, space } from "@shared/ui.design-system";

interface MarkerOptions {
  textColor?: string;
  fontSize?: number | string;
  title?: string;
  icon?: React.ReactNode;
  iconScale?: number;
}

interface MarkerProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  active?: boolean;
  text?: string | number;
  options?: MarkerOptions;
}

export const Marker: React.FC<MarkerProps> = ({
  size = 20,
  active = false,
  text,
  options = {},
  ...otherProps
}) => {
  const {
    textColor = colours.primary.dark,
    fontSize = space[12],
    title,
    icon,
    iconScale = 1.4
  } = options;

  if (!(active || icon) && !text) {
    return null;
  }

  const computedFontSize = fontSize ?? Math.round(size / 2.5);
  const computedStrokeWidth = Math.max(1, Math.round(size / 20));
  const ariaLabel =
    title ?? (text ? `Marker with ${text}` : "Marker with icon");
  const transform = `translate(10,10) scale(${iconScale}) translate(-10,-10)`;
  const fillColor = active ? colours.accent.medium : colours.secondary.strong;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block" }}
      {...otherProps}
    >
      <title>{ariaLabel}</title>
      <circle
        cx="10"
        cy="10"
        r="9.5"
        fill={fillColor}
        stroke={fillColor}
        strokeWidth={computedStrokeWidth}
      />
      {text ? (
        <text
          x="50%"
          y="50%"
          fill={textColor}
          fontSize={computedFontSize}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
          dy="0.05em"
        >
          {text}
        </text>
      ) : (
        <g transform={transform}>
          {icon ?? (
            <path
              d="M13.7203 7.65999L13.0003 7.21999V6.82499C13.0017 6.69894 12.974 6.57428 12.9193 6.46068C12.8647 6.34708 12.7846 6.24761 12.6853 6.16999C12.5887 6.09164 12.4749 6.03721 12.3533 6.01115C12.2316 5.98508 12.1056 5.98811 11.9853 6.01999C11.7682 6.07561 11.5572 6.15265 11.3553 6.24999L10.1753 5.56499C10.1309 5.54048 10.081 5.52762 10.0303 5.52762C9.9796 5.52762 9.92971 5.54048 9.88531 5.56499L8.72531 6.24999C8.52248 6.14575 8.30976 6.062 8.09031 5.99999C7.9709 5.96771 7.84551 5.96449 7.7246 5.99058C7.60369 6.01668 7.49078 6.07134 7.39531 6.14999C7.296 6.22761 7.21591 6.32708 7.16128 6.44068C7.10665 6.55428 7.07894 6.67894 7.08031 6.80499V7.19999L6.34031 7.63999C6.29639 7.66534 6.25989 7.70177 6.23445 7.74564C6.20901 7.7895 6.19551 7.83928 6.19531 7.88999V12.075C6.19563 12.1263 6.20913 12.1768 6.23452 12.2214C6.25991 12.2661 6.29634 12.3035 6.34031 12.33L7.93031 13.265L9.86031 14.405C9.90423 14.4309 9.9543 14.4446 10.0053 14.4446C10.0563 14.4446 10.1064 14.4309 10.1503 14.405L12.0803 13.265H12.1053L13.6953 12.33C13.7393 12.3035 13.7757 12.2661 13.8011 12.2214C13.8265 12.1768 13.84 12.1263 13.8403 12.075V7.90999C13.8428 7.86161 13.8331 7.81338 13.8121 7.76971C13.7912 7.72604 13.7596 7.68832 13.7203 7.65999ZM13.0003 8.04499V7.66499L13.3203 7.85499L13.0003 8.04499ZM10.2153 14V10.115L11.6353 9.27499L11.7553 9.32499V13.065L11.2203 13.385L10.2153 14ZM7.39531 9.23999C7.53687 9.35413 7.71347 9.41594 7.89531 9.41499H7.93531V12.85L6.57531 12.05V8.18499L7.07531 8.48499V8.57999C7.07527 8.70704 7.10404 8.83244 7.15947 8.94676C7.2149 9.06108 7.29553 9.16134 7.39531 9.23999ZM9.39531 8.15999C9.03134 8.58105 8.54273 8.87527 8.00031 8.99999C7.93662 9.01577 7.87017 9.01676 7.80604 9.00289C7.7419 8.98903 7.68179 8.96067 7.63031 8.91999C7.57883 8.87482 7.53718 8.81956 7.50794 8.75764C7.47869 8.69571 7.46248 8.62844 7.46031 8.55999V6.82499C7.46062 6.75692 7.47603 6.68978 7.50542 6.62839C7.53482 6.567 7.57748 6.5129 7.63031 6.46999C7.70467 6.40804 7.79854 6.37439 7.89531 6.37499C7.93014 6.37013 7.96548 6.37013 8.00031 6.37499C8.54418 6.49907 9.03345 6.79544 9.39531 7.21999C9.37373 7.27066 9.36186 7.32493 9.36031 7.37999V7.99999C9.36186 8.05504 9.37373 8.10931 9.39531 8.15999ZM8.30531 9.32499H8.34531L8.42531 9.28999L9.84031 10.13V14L8.30531 13.065V9.32499ZM10.6603 7.24499C11.0209 6.82135 11.5083 6.52507 12.0503 6.39999C12.1139 6.38302 12.1806 6.38143 12.245 6.39534C12.3093 6.40925 12.3694 6.43827 12.4203 6.47999C12.474 6.52216 12.5172 6.57611 12.5467 6.63767C12.5762 6.69923 12.5911 6.76674 12.5903 6.83499V8.57999C12.5908 8.6489 12.5757 8.71702 12.5463 8.77933C12.5169 8.84164 12.4738 8.89655 12.4203 8.93999C12.3683 8.9797 12.3082 9.00746 12.2442 9.02129C12.1803 9.03512 12.1141 9.03467 12.0503 9.01999C11.5084 8.89158 11.0215 8.59385 10.6603 8.16999C10.6859 8.11709 10.6979 8.05868 10.6953 7.99999V7.40499C10.6963 7.34966 10.6843 7.29486 10.6603 7.24499ZM10.3203 7.41499V7.98999L9.73531 7.99999V7.41499H10.3203ZM10.3803 6.99999L10.3453 7.04499H9.71031L9.68031 6.99999C9.51138 6.80695 9.31614 6.63863 9.10031 6.49999L10.0303 5.95499L10.9553 6.49999C10.7408 6.63842 10.5472 6.80678 10.3803 6.99999ZM9.68031 8.42499L9.71031 8.38499H10.3453L10.3803 8.42499C10.6094 8.70092 10.8895 8.9301 11.2053 9.09999L10.0303 9.79499L8.85031 9.08999C9.16751 8.92001 9.44925 8.69089 9.68031 8.41499V8.42499ZM12.1303 9.42499H12.1703C12.3522 9.42594 12.5288 9.36413 12.6703 9.24999C12.7733 9.17132 12.8566 9.06991 12.9139 8.95368C12.9711 8.83745 13.0007 8.70955 13.0003 8.57999V8.49999L13.5003 8.19999V12.06L12.1403 12.86L12.1303 9.42499ZM6.75531 7.85499L7.08031 7.66499V8.04499L6.75531 7.85499Z"
              fill="black"
            />
          )}
        </g>
      )}
    </svg>
  );
};
